import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { BetaChip } from 'elements/chip/BetaChip'
import { VerificationWarningIcon } from 'elements/VerificationWarningIcon'
import { premiumProductsSelectors } from 'ducks/premiumProducts'
import { usePremiumProducts } from 'hooks/usePremiumProducts'
import { BarChartSquareIcon, GoogleIcon, Icon } from 'opensolar-ui'
import { GoogleLeadChip } from 'pages/googleLeads/elements/ChipsAndBanners'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { StructType, useStructTypes } from 'resources/structs/hooks/useStructTypes'
import appStorage from 'storage/appStorage'
import { StaffPermissions } from 'types/auth'
import { PermissionKey, PermissionSet } from 'types/roles'
import { RootState } from 'types/state'
import { WALLET_PRODUCT_TITLE_AUDIT_TRAIL } from 'resources/premiumProducts/constants'
import { useFeatureFlag } from 'util/featureFlags'

const PRO_DASHBOARD_CUSTOM_FEATURE_KEY = 'data_product_pro_custom'
const PRO_DASHBOARD_FEATURE_KEY = 'data_product_pro'

export const useMenuData = (uiKey: string): MenuItem[] => {
  const accessRightsSetting = useSelector(permissionsSelectors.getPermissionsSetting)
  const orgId = useSelector(authSelectors.getOrgId)

  const partnerEnabled = useSelector(orgSelectors.getEnableExhibit) // This field/selector is misnamed, `enabled_exhibit` really means they're any kind of partner
  const proFeaturesEnabled = useSelector(orgSelectors.getProFeaturesEnabled)
  const showcaseEnabled = useSelector(orgSelectors.getEnableShowcase)
  const supplierBaseEnabled = useSelector((state: RootState) =>
    orgSelectors.getSupplierFeature(state, 'is_supplier_base_enabled')
  )
  const productUploadEnabled = useSelector((state: RootState) =>
    orgSelectors.getSupplierFeature(state, 'is_product_upload_enabled')
  )
  const quoteReviewEnabled = useSelector((state: RootState) =>
    orgSelectors.getSupplierFeature(state, 'is_quote_review_enabled')
  )
  const shopApiDocsEnabled = useSelector((state: RootState) =>
    orgSelectors.getSupplierFeature(state, 'is_shop_api_docs_enabled')
  )
  const sellerEnabled = supplierBaseEnabled || productUploadEnabled || quoteReviewEnabled || shopApiDocsEnabled
  const expoFlagEnabled = useFeatureFlag('expo', 'on')
  const expoEnabled = useSelector(orgSelectors.getExpoEnabled) && !!expoFlagEnabled
  const marketingServicesEnabled = showcaseEnabled || expoEnabled

  const isNearmapOrg = useSelector(orgSelectors.getIsNearmapOrg)
  const isStaff = useSelector(authSelectors.getIsStaff)
  const isSuperUser = useSelector(authSelectors.getIsSuperUser)
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const staffPermissions = useSelector(authSelectors.getStaffPermissions)
  const googleLeadsEnabled = !!useFeatureFlag('enable_google_leads_program', 'enabled')
  const profilePageEnabled = !!useFeatureFlag('enable_profile_leads_page', 'enabled')
  const schedulingV3Enabled = !!useFeatureFlag('scheduling_v3', 'on')
  const centralizedInternalNotificationsEnabled = !!useFeatureFlag('centralized_internal_notifications', 'on')
  const adaStudioTemplatesEnabled = !!useFeatureFlag('ada_studio_templates', 'on')
  const leadGenAiEnabled = !!useFeatureFlag('lead_gen_ai', 'on')
  const orgVerificationEnabled = !!useFeatureFlag('enable_org_verification', 'on')

  const proHardwareDistributorEnabled = !!useFeatureFlag('pro_hardware_distributor', 'on')
  const proHardwareSupplierPreferenceEnabled = !!useFeatureFlag('supplier_preference_settings', 'on')

  const structTypes = useStructTypes()

  const customFormsEnabled = useFeatureFlag('custom_forms', 'on') || isStaff
  const connectedOrgsEnabled = !!useFeatureFlag('connected_orgs', 'on')

  const enableWalletFeature = useFeatureFlag('os_wallet', 'on')
  const walletEnabled = !!(enableWalletFeature && isAdmin)

  const currentOrg = useSelector(authSelectors.getCurrentOrg)
  const shouldShowVerificationWarning = orgVerificationEnabled ? !Boolean(currentOrg?.is_org_verified) : false

  const { productsLoading, availableProducts } = usePremiumProducts()
  const loadedOrgId = useSelector(premiumProductsSelectors.getLoadedOrgId)
  const walletProductsReadyForOrg =
    !!orgId && !productsLoading && loadedOrgId === orgId && !!availableProducts

  const securityAuditingEnabled =
    walletProductsReadyForOrg &&
    !!availableProducts?.some(
      (product) => product.is_active && product.title === WALLET_PRODUCT_TITLE_AUDIT_TRAIL
    )
  const adsEnabled =
    walletProductsReadyForOrg &&
    !!availableProducts?.some((product) => product.is_active && product.title.includes('Ads'))
  const marketDataReportProducts = walletProductsReadyForOrg
    ? availableProducts?.filter(
        (product) => product.is_active && product.feature_key?.startsWith('data_product_')
      )
    : undefined
  const reportingAndInsightsEnabled = !!marketDataReportProducts?.length
  const proDashboardCustomEnabled = !!marketDataReportProducts?.some(
    (product) => product.feature_key === PRO_DASHBOARD_CUSTOM_FEATURE_KEY
  )
  const proDashboardEnabled = !!marketDataReportProducts?.some(
    (product) => product.feature_key === PRO_DASHBOARD_FEATURE_KEY
  )

  return useMemo(() => {
    if (orgId && accessRightsSetting) {
      // Get base menu data
      let menuData = getMenuData(orgId, uiKey, structTypes)

      // Add warning icons if feature enabled and org not verified
      if (shouldShowVerificationWarning && isAdmin) {
        menuData = menuData.map((category) => {
          let updatedCategory = { ...category }

          if (category.title === 'Company') {
            updatedCategory.warningIcon = <VerificationWarningIcon />

            if (updatedCategory.cards) {
              updatedCategory.cards = updatedCategory.cards.map((card) => {
                if (card.title === 'Authentication') {
                  return {
                    ...card,
                    warningIcon: <VerificationWarningIcon />,
                  }
                }
                return card
              })
            }
          }

          return updatedCategory
        })
      }

      const context: MenuDataContext = {
        walletEnabled,
        marketingServicesEnabled,
        partnerEnabled,
        proFeaturesEnabled,
        sellerEnabled,
        supplierBaseEnabled,
        productUploadEnabled,
        quoteReviewEnabled,
        shopApiDocsEnabled,
        connectedOrgsEnabled,
        customFormsEnabled,
        showcaseEnabled,
        isStaff,
        isSuperUser,
        isAdmin,
        isNearmapOrg,
        accessRightsSetting,
        expoEnabled,
        googleLeadsEnabled,
        profilePageEnabled,
        adaStudioTemplatesEnabled: adaStudioTemplatesEnabled || leadGenAiEnabled,
        proHardwareDistributorEnabled,
        proHardwareSupplierPreferenceEnabled,
        securityAuditingEnabled,
        adsEnabled,
        reportingAndInsightsEnabled,
        proDashboardEnabled,
        proDashboardCustomEnabled,
        staffPermissions,
        schedulingV3Enabled,
        centralizedInternalNotificationsEnabled,
      }

      const ret = menuData.map((section) => showCard(section, context))

      return ret
    } else {
      return []
    }
  }, [
    orgId,
    structTypes,
    walletEnabled,
    partnerEnabled,
    connectedOrgsEnabled,
    customFormsEnabled,
    showcaseEnabled,
    isStaff,
    isSuperUser,
    isAdmin,
    isNearmapOrg,
    accessRightsSetting,
    googleLeadsEnabled,
    profilePageEnabled,
    adsEnabled,
    reportingAndInsightsEnabled,
    proDashboardEnabled,
    proDashboardCustomEnabled,
    structTypes,
    expoEnabled,
    orgVerificationEnabled,
    currentOrg,
    adaStudioTemplatesEnabled,
    proHardwareDistributorEnabled,
    staffPermissions,
    schedulingV3Enabled,
    centralizedInternalNotificationsEnabled,
    securityAuditingEnabled,
    productsLoading,
    loadedOrgId,
    availableProducts,
    uiKey,
  ])
}

type MenuDataContext = {
  walletEnabled: boolean
  connectedOrgsEnabled: boolean
  customFormsEnabled: boolean
  showcaseEnabled: boolean
  expoEnabled: boolean
  marketingServicesEnabled: boolean
  partnerEnabled: boolean
  supplierBaseEnabled: boolean
  productUploadEnabled: boolean
  quoteReviewEnabled: boolean
  shopApiDocsEnabled: boolean
  proFeaturesEnabled: boolean
  sellerEnabled: boolean
  isStaff: boolean
  isSuperUser: boolean
  isAdmin: boolean
  isNearmapOrg: boolean
  accessRightsSetting: PermissionSet
  googleLeadsEnabled: boolean
  profilePageEnabled: boolean
  adaStudioTemplatesEnabled: boolean
  proHardwareDistributorEnabled: boolean
  proHardwareSupplierPreferenceEnabled: boolean
  securityAuditingEnabled: boolean
  adsEnabled: boolean
  reportingAndInsightsEnabled: boolean
  proDashboardEnabled: boolean
  proDashboardCustomEnabled: boolean
  staffPermissions?: StaffPermissions
  schedulingV3Enabled: boolean
  centralizedInternalNotificationsEnabled: boolean
}

const showCard = (section: MenuItem, context: MenuDataContext): ShowableMenuItem => {
  let cards: ShowableMenuItem[] | undefined
  if (section && section.cards) {
    cards = section.cards.map((card) => showCard(card, context))
  }

  let shouldShow =
    (!section.requirePartnerFeature || context.partnerEnabled) &&
    (!section.requireProFeatures || context.proFeaturesEnabled) &&
    (!section.requireProFeaturesUnlessStaff || context.isStaff || context.proFeaturesEnabled) &&
    (!section.requireMarketingServices || context.marketingServicesEnabled) &&
    (!section.requireShowcase || context.showcaseEnabled) &&
    (!section.requireExpo || context.expoEnabled) &&
    (!section.requireCustomForms || context.customFormsEnabled) &&
    (!section.requireSuperUser || context.isSuperUser) &&
    (!section.requireAdmin || context.isAdmin) &&
    (!section.requireStaff || context.isStaff) &&
    (!section.requireWallet || context.walletEnabled) &&
    (!section.requireConnectedOrgs || context.connectedOrgsEnabled) &&
    (!section.requireNearmapOrg || context.isNearmapOrg) &&
    (!section.requireGoogleLeads || context.googleLeadsEnabled) &&
    (!section.requireProfile || context.profilePageEnabled) &&
    (!section.requireSeller || context.sellerEnabled) &&
    (!section.requireSupplierBaseFeature || context.supplierBaseEnabled) &&
    (!section.requireProductUploadFeature || context.productUploadEnabled) &&
    (!section.requireQuoteReviewFeature || context.quoteReviewEnabled) &&
    (!section.requireShopApiDocsFeature || context.shopApiDocsEnabled) &&
    (!section.requireAdaStudioTemplates || context.adaStudioTemplatesEnabled) &&
    (!section.requireProHardwareDistributor || context.proHardwareDistributorEnabled) &&
    (!section.proHardwareSupplierPreferenceEnabled || context.proHardwareSupplierPreferenceEnabled) &&
    (!section.requireAds || context.adsEnabled) &&
    (!section.requireReportingAndInsights || context.reportingAndInsightsEnabled) &&
    (!section.requireProDashboard || context.proDashboardEnabled) &&
    (!section.requireProDashboardCustom || context.proDashboardCustomEnabled) &&
    hasAssessRight(section.requireAccessRight, context.accessRightsSetting) &&
    // filter parent cards that are now empty
    (!cards || cards.some((card) => card.shouldShow)) &&
    (!section.requireSecurityAuditing || context.securityAuditingEnabled) &&
    (!section.requireSchedulingV3 || context.schedulingV3Enabled) &&
    (!section.requireCentralizedNotifications || context.centralizedInternalNotificationsEnabled) &&
    (!section.requireStaffPermission ||
      (context.isStaff && (context.isSuperUser || !!context.staffPermissions?.[section.requireStaffPermission])))

  return {
    ...section,
    shouldShow,
    cards,
  }
}

export const hasAssessRight = (key: PermissionKey | undefined, accessRightsSetting: PermissionSet): boolean => {
  if (!key) {
    return true
  } else if (accessRightsSetting && accessRightsSetting[key] && accessRightsSetting[key]['view']) {
    return true
  } else {
    return false
  }
}

export const getMenuData = (orgId: number, uiKey: string, structTypes: StructType[] | undefined): MenuItem[] => [
  {
    icon: <Icon icon="dashboard" />,
    title: 'Dashboard',
    uiKey: uiKey + 'dashboard',
    cards: [
      {
        title: 'Welcome',
        url: '/control',
        uiKey: uiKey + 'dashboard.welcome',
      },
      {
        title: 'Calendar',
        url: '/activities/calendar',
        uiKey: uiKey + 'dashboard.calendar',
        requireSchedulingV3: true,
        requireAccessRight: 'activities',
      },
      {
        title: 'Scheduling',
        url: '/activities/scheduling',
        uiKey: uiKey + 'dashboard.scheduling',
        requireSchedulingV3: true,
        requireAccessRight: 'activities',
      },
    ],
  },
  {
    icon: <Icon icon="corporate_fare" />,
    title: 'Company',
    uiKey: uiKey + 'company',
    cards: [
      {
        title: 'Business Info',
        url: '/content/' + orgId,
        uiKey: uiKey + 'company.business_info',
      },
      {
        title: 'Team',
        uiKey: uiKey + 'company.team',
        cards: [
          {
            title: 'Team Members',
            url: '/roles',
            uiKey: uiKey + 'company.team.team_members',
          },
          {
            title: 'Custom Roles',
            url: '/permissions_role',
            requireAdmin: true,
            uiKey: uiKey + 'company.team.custom_roles',
          },
          {
            title: 'Connected Organisations',
            url: '/connected_orgs/',
            requireConnectedOrgs: true,
            uiKey: uiKey + 'company.team.connected_orgs',
          },
          {
            title: 'Sharing Permissions',
            url: '/connected_orgs_roles',
            requireConnectedOrgs: true,
            uiKey: uiKey + 'company.team.sharing_permissions',
          },
        ],
      },
      {
        title: 'Business Process',
        uiKey: uiKey + 'company.business_process',
        requireProFeaturesUnlessStaff: true,
        cards: [
          {
            title: 'Project Workflows',
            url: '/workflows',
          },
          {
            title: 'Project Tags',
            url: '/tags',
          },
          {
            title: 'Custom Fields',
            url: '/custom_fields',
          },
          {
            title: 'Custom Forms',
            url: '/custom_forms',
            requireCustomForms: true,
          },
        ],
      },
      {
        title: 'PROfile',
        url: '/profile_page',
        uiKey: uiKey + 'other.profile_page',
        requireProfile: true,
        requireAdmin: true,
      },
      {
        title: 'Settings',
        url: '/orgs/' + orgId,
        uiKey: uiKey + 'company.settings',
        requireProFeaturesUnlessStaff: true,
      },
      {
        title: 'Notifications',
        url: '/company_notifications',
        uiKey: uiKey + 'company.notifications',
        requireCentralizedNotifications: true,
        requireAdmin: true,
        requireProFeaturesUnlessStaff: true,
      },
      {
        title: 'Authentication',
        url: '/configure-mfa',
        requireAdmin: true,
        requireProFeatures: true,
      },
      {
        title: 'Partner Settings',
        url: '/partner_settings',
        requireAdmin: true,
        requirePartnerFeature: true,
        uiKey: uiKey + 'company.partner_settings',
      },
    ],
  },
  {
    icon: <Icon icon="local_atm" />,
    title: 'Pricing & Payments',
    uiKey: uiKey + 'pricing_and_payments',
    requireProFeatures: true,
    cards: [
      {
        title: 'Price Schemes',
        url: '/pricing_schemes',
        uiKey: uiKey + 'pricing_and_payments.pricing_schemes',
      },
      {
        title: 'Cost Schemes',
        url: '/costings',
        uiKey: uiKey + 'pricing_and_payments.cost_information',
      },
      {
        title: 'Adders Library',
        url: '/adders',
        uiKey: uiKey + 'pricing_and_payments.adders',
      },
      {
        title: 'Payment Options',
        url: '/payment_options',
        uiKey: uiKey + 'pricing_and_payments.payment_options',
      },
      {
        title: 'Commissions',
        url: '/commissions',
        uiKey: uiKey + 'pricing_and_payments.commissions',
      },
    ].concat(
      appStorage.getString('feature_preview')
        ? [
            {
              title: 'Upload & Import CSV',
              url: '/csv_upload?model=projects',
              uiKey: uiKey + 'pricing_and_payments.csv_upload',
            },
          ]
        : []
    ),
  },
  {
    icon: <Icon icon="design_services" />,
    title: 'Design & Hardware',
    uiKey: uiKey + 'design_and_hardware',
    requireProFeatures: true,
    cards: [
      {
        title: 'Manage Suppliers',
        url: '/shop_suppliers',
        uiKey: uiKey + 'design_and_hardware.hardware_suppliers',
        requireAdmin: true,
        requireProHardwareDistributor: true,
      },
      {
        title: 'Supplier Preferences',
        url: '/hardware_supplier_preferences',
        uiKey: uiKey + 'design_and_hardware.supplier_preference',
        requireAdmin: true,
        proHardwareSupplierPreferenceEnabled: true,
      },
      {
        title: 'Modules',
        url: '/component_module_activations',
        uiKey: uiKey + 'design_and_hardware.component_module_activations',
      },
      {
        title: 'Inverters',
        url: '/component_inverter_activations',
        uiKey: uiKey + 'design_and_hardware.component_inverter_activations',
      },
      {
        title: 'Batteries',
        url: '/component_battery_activations',
        uiKey: uiKey + 'design_and_hardware.component_battery_activations',
      },
      {
        title: 'Other Components',
        url: '/component_other_activations',
        uiKey: uiKey + 'design_and_hardware.component_other_activations',
      },
      {
        title: 'Setbacks & Design Settings',
        url: '/project_configurations',
        uiKey: uiKey + 'design_and_hardware.setbacks_and_design_settings',
      },
      {
        title: 'Battery Control Schemes',
        url: '/battery_schemes',
        uiKey: uiKey + 'design_and_hardware.battery_schemes',
      },
      {
        title: 'Auto Design Templates',
        url: '/system_templates',
        uiKey: uiKey + 'design_and_hardware.system_templates',
        requireAdaStudioTemplates: true,
        requireAccessRight: 'auto_design_templates',
        chip: <BetaChip size="xSmall" />,
      },
    ],
  },
  {
    icon: <Icon icon="shopping_basket" />,
    title: 'Purchase Experience',
    uiKey: uiKey + 'purchase_experience',
    requireProFeatures: true,
    cards: [
      {
        title: 'Proposal Template',
        url: '/proposal_templates',
        uiKey: uiKey + 'purchase_experience.proposal_templates',
      },
      {
        title: 'Contract Template',
        url: '/contracts',
        uiKey: uiKey + 'purchase_experience.contracts',
      },
      {
        title: 'Checkout Experience',
        url: '/customer_checkout/' + orgId,
        uiKey: uiKey + 'purchase_experience.checkout_exp',
      },
      {
        title: 'Case Studies',
        url: '/testimonials',
        uiKey: uiKey + 'purchase_experience.case_studies',
      },
    ],
  },
  {
    icon: <Icon icon="event_note" />,
    title: 'Customers',
    uiKey: uiKey + 'customers',
    requireProFeatures: true,
    cards: [
      {
        title: 'Contacts',
        url: '/contacts',
        uiKey: uiKey + 'customers.contacts',
      },
      {
        title: 'Transactions',
        url: '/transactions',
        uiKey: uiKey + 'customers.transactions',
      },
      {
        title: 'Files',
        url: '/private_files',
        uiKey: uiKey + 'customers.private_files',
      },
      {
        title: 'Proposal Viewers',
        url: '/proposal_viewers',
        uiKey: uiKey + 'customers.proposal_viewers',
      },
      {
        title: 'Activities',
        url: '/events',
        uiKey: uiKey + 'customers.activities',
      },
    ],
  },
  {
    icon: <Icon icon="storefront" />,
    title: 'Supplier',
    uiKey: uiKey + 'supplier',
    requireSeller: true,
    cards: [
      {
        title: 'Upload Products',
        url: '/supplier/product_upload',
        uiKey: uiKey + 'suppliers.product_upload',
        requireProductUploadFeature: true,
      },
      {
        title: 'Price Tier',
        url: '/supplier/price_tier',
        uiKey: uiKey + 'suppliers.price_tier',
        requireSupplierBaseFeature: true,
      },
      {
        title: 'Stock Locations',
        url: '/supplier/stock_locations',
        uiKey: uiKey + 'suppliers.stock_locations',
        requireSupplierBaseFeature: true,
      },
      {
        title: 'Configuration',
        url: '/supplier/configuration',
        uiKey: uiKey + 'suppliers.configuration',
        requireSupplierBaseFeature: true,
        requireAdmin: true,
      },
      {
        title: 'Customers',
        url: '/customers',
        uiKey: uiKey + 'suppliers.customers',
        requireSupplierBaseFeature: true,
      },
      {
        title: 'Invite Customers',
        url: '/supplier/invite_customers',
        uiKey: uiKey + 'suppliers.invite_customers',
        requireSupplierBaseFeature: true,
      },
      {
        title: 'Shop API Docs',
        url: 'https://developers.opensolar.com/seller/',
        uiKey: uiKey + 'suppliers.shop_api_docs',
        requireShopApiDocsFeature: true,
        externalLink: true,
      },
      {
        title: 'Quotes',
        url: '/supplier_quotes',
        requireQuoteReviewFeature: true,
        uiKey: uiKey + 'suppliers.quotes',
      },
      {
        title: 'Deals',
        url: '/deals',
        uiKey: uiKey + 'suppliers.deals',
        requireSupplierBaseFeature: true,
        requireAdmin: true,
      },
    ],
  },
  {
    icon: <Icon icon="handshake" />,
    title: 'Marketing Services',
    uiKey: uiKey + 'marketing_services',
    requireMarketingServices: true,
    cards: [
      {
        title: 'Showcase Content',
        url: '/component_content',
        requireShowcase: true,
        requireAdmin: true,
        uiKey: uiKey + 'marketing_services.showcase_content',
      },
      {
        title: 'Expo Stands',
        url: '/expo_stands',
        requireExpo: true,
        requireAdmin: true,
        uiKey: uiKey + 'marketing_services.expo_content',
      },
    ],
  },
  {
    icon: <BarChartSquareIcon width="24px" height="24px" />,
    title: 'Reporting & Insights',
    uiKey: uiKey + 'reporting_and_insights',
    requireReportingAndInsights: true,
    cards: [
      {
        title: 'Org Data Report',
        url: '/market_data_report_dashboard',
        uiKey: uiKey + 'reporting_and_insights.org_data_report',
        requireProDashboard: true,
        chip: <BetaChip size="xSmall" />,
      },
      {
        title: 'Market Data Report',
        url: '/market_data_report_dashboard',
        uiKey: uiKey + 'reporting_and_insights.market_data_report',
        requireProDashboardCustom: true,
        chip: <BetaChip size="xSmall" />,
      },
    ],
  },
  {
    icon: <Icon icon="power" />,
    title: 'Integrations',
    uiKey: uiKey + 'integrations',
    requireProFeatures: true,
    cards: [
      {
        title: 'Your Wallet',
        url: '/wallet',
        uiKey: uiKey + 'integrations.wallets',
        requireWallet: true,
      },
      {
        title: 'Integrations & Services',
        url: '/external_api_keys/' + orgId,
        uiKey: uiKey + 'integrations.api_keys',
        requireAccessRight: 'integrations_and_api_keys',
      },
      {
        title: 'Webhooks',
        url: '/webhooks',
        uiKey: uiKey + 'integrations.webhooks',
      },
    ],
  },
  {
    icon: <Icon icon="ads_click" />,
    title: 'Ads',
    uiKey: uiKey + 'ads',
    requireSeller: false,
    requireAds: true,
    cards: [
      {
        title: 'Ad Campaigns',
        url: '/ads/ad_campaigns',
        uiKey: uiKey + 'ads.ad_campaigns',
      },
      {
        title: 'Ad Creatives',
        url: '/ads/ad_creatives',
        uiKey: uiKey + 'ads.ad_creatives',
      },
      {
        title: 'Ad Placements',
        url: '/ads/ad_placements',
        uiKey: uiKey + 'ads.ad_placements',
        requireStaff: true,
      },
      {
        title: 'Ad Sites',
        url: '/ads/ad_sites',
        uiKey: uiKey + 'ads.ad_sites',
        requireStaff: true,
      },
      {
        title: 'Ad Formats',
        url: '/ads/ad_formats',
        uiKey: uiKey + 'ads.ad_formats',
        requireSuperUser: true,
      },
    ],
  },
  {
    icon: <Icon icon="widgets" />,
    title: 'Other',
    uiKey: uiKey + 'other',
    requireProFeatures: true,
    cards: [
      {
        title: 'Utility Tariffs',
        url: '/utility_tariff_groups',
        uiKey: uiKey + 'other.utility_tariffs',
      },
      {
        title: 'Incentives',
        url: '/incentives',
        uiKey: uiKey + 'other.incentives',
      },
      {
        title: 'Document Templates',
        url: '/document_templates',
        uiKey: uiKey + 'other.document_templates',
      },
      {
        title: 'Lead Capture',
        url: '/lead_capture_forms',
        uiKey: uiKey + 'other.lead_capture_forms',
      },
      {
        title: 'Sales Territories',
        url: '/sales_territories',
        uiKey: uiKey + 'other.sales_territories',
      },
      {
        title: 'Public Files',
        url: '/public_files',
        uiKey: uiKey + 'other.public_files',
      },
      {
        title: 'Audit Trail',
        uiKey: uiKey + 'other.audit_trail',
        requireSecurityAuditing: true,
        cards: [
          {
            title: 'API Usage',
            url: '/api_usage',
            uiKey: uiKey + 'other.audit_trail.api_usage',
          },
          {
            title: 'API Access Logs',
            url: '/api_request_logs',
            uiKey: uiKey + 'other.audit_trail.api_access_logs',
          },
          {
            title: 'User Sessions',
            url: '/user_sessions',
            uiKey: uiKey + 'other.audit_trail.user_sessions',
          },
        ],
      },
    ],
  },
  {
    icon: <GoogleIcon size={20} />,
    chip: (
      <GoogleLeadChip
        id="control-menu__google-chip"
        sx={{
          maxWidth: '100%',
          width: '100%',
          height: 'auto',
          '& .MuiChip-label': {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            display: 'block',
          },
        }}
      />
    ),
    title: 'Google Leads',
    requireGoogleLeads: true,
    uiKey: uiKey + 'google_leads',
    cards: [
      {
        title: 'Lead Generation Page',
        url: `/google_leads_config/`,
      },
    ],
  },
  {
    isDivider: true,
    title: '',
    requireStaff: true,
  },
  {
    icon: <Icon icon="account_box" />,
    title: 'Staff',
    requireStaff: true,
    uiKey: uiKey + 'staff',
    cards: [
      {
        title: 'Manufacturers',
        url: '/manufacturers',
      },
      {
        title: 'Modules',
        url: '/component_modules',
      },
      {
        title: 'Inverters',
        url: '/component_inverters',
      },
      {
        title: 'Batteries',
        url: '/component_batteries',
      },
      {
        title: 'Other Components',
        url: '/component_others',
      },
      {
        title: 'Utilities',
        url: '/utilities',
      },
      {
        title: 'Distributors',
        url: '/distributors',
      },
      {
        title: 'Network Tariffs',
        url: '/network_tariff_groups',
      },
      {
        title: 'Hardware Suppliers',
        url: '/global_hardware_suppliers',
      },
      {
        title: 'Supplier Payment Methods',
        url: '/global_supplier_payment_methods',
      },
      {
        title: 'CSV Upload History',
        url: '/csv_file_upload_requests',
      },
      {
        title: 'Global Orgs',
        url: '/global_orgs',
      },
      {
        title: 'Global Roles',
        url: '/global_roles',
      },
      {
        title: 'Global Projects',
        url: '/global_projects',
      },
      {
        title: 'Global Public Files',
        url: '/global_public_files',
      },
      {
        title: 'Staff Reports',
        url: '/staff_reports',
      },
      {
        title: 'Feedback Dashboard',
        url: '/feedback',
        requireStaff: true,
        uiKey: uiKey + 'staff.feedback',
      },
      {
        title: 'Feature Configs',
        url: '/config_save',
      },
      {
        title: 'Security Hub',
        url: '/staff/security',
        requireStaff: true,
        uiKey: uiKey + 'staff.security',
        cards: [
          {
            title: 'Staff Audit Trail',
            url: '/staff/audit_trail',
          },
          {
            title: 'Deactivate Users',
            url: '/staff/deactivate_users',
          },
          {
            title: 'Reactivate Users',
            url: '/staff/reactivate_users',
          },
          {
            title: 'Suspicious activity',
            url: '/staff/suspicious_activity',
          },
          {
            title: 'Suspicion signals',
            url: '/staff/suspicion_signals',
          },
          {
            title: 'Throttle inspection',
            url: '/staff/throttle_inspection',
          },
        ],
      },
      {
        title: 'Operations Hub',
        url: '/staff/operations',
        requireStaff: true,
        uiKey: uiKey + 'staff.operations',
        cards: [
          {
            title: 'Scheduled Events',
            url: '/scheduled_events',
          },
          {
            title: 'Email delivery',
            url: '/staff/emails',
            requireStaff: true,
            uiKey: uiKey + 'staff.emails',
          },
          {
            title: 'Query runner',
            url: '/staff/query_runner',
            requireStaff: true,
            uiKey: uiKey + 'staff.query_runner',
          },
          {
            title: 'Trails analyzer',
            url: '/staff/trails_analyzer',
            requireStaff: true,
            uiKey: uiKey + 'staff.trails_analyzer',
          },
        ],
      },
      {
        title: 'Org Verification',
        url: '/org_verification',
      },
      {
        title: 'Structured Configs',
        uiKey: uiKey + 'staff.structs',
        cards: structTypes
          ? (() => {
              const singularStructTypes = structTypes.filter((t) => t.singular)
              const nonSingularStructTypes = structTypes.filter((t) => !t.singular)
              const singularCard =
                singularStructTypes.length > 0
                  ? [
                      {
                        title: 'Single Configs',
                        url: '/structs/singular',
                        uiKey: uiKey + 'staff.structs.singular',
                      },
                    ]
                  : []
              const typeCards = nonSingularStructTypes.map((structType) => ({
                title: structType.title,
                url: '/structs/' + structType.id,
                uiKey: uiKey + 'staff.structs.' + structType.id,
              }))
              return [...singularCard, ...typeCards]
            })()
          : [
              {
                title: 'Loading Structs...',
              },
            ],
      },
      {
        title: 'Landing Pages',
        url: '/landing_pages',
      },
      {
        title: 'UI Library',
        url: '/ui_library',
      },
      {
        title: 'Payment Methods',
        url: '/payment_methods',
      },
      {
        title: 'Partner Records',
        url: '/partner_data_matching_records',
      },
      {
        title: 'CashFlow Pricing Activations',
        url: '/cashflow_pricing_schedule_activations',
      },
      {
        title: 'Expo Homepage Highlights',
        url: '/expo_homepages',
      },
      {
        title: 'Expo Brands',
        url: '/brands',
      },
      {
        title: 'Integration & Service Products',
        url: '/superuser_wallet_products',
        requireStaffPermission: 'wallet_products',
      },
      {
        title: 'Integration & Service Variants',
        url: '/superuser_wallet_product_variants',
        requireStaffPermission: 'wallet_products',
      },
      {
        title: 'Global Wallet Activity',
        url: '/global_wallet_transactions',
        requireStaffPermission: 'global_wallet_activity',
      },
      {
        title: 'Finance Integrations',
        url: '/finance_partners',
        requireStaffPermission: 'finance_integrations',
      },
      {
        title: 'CashFlow Pricing Schedules',
        url: '/cashflow_pricing_schedules',
        requireStaffPermission: 'cashflow_pricing',
      },
      {
        title: 'CashFlow Setup Checkpoints',
        url: '/cashflow_setup_checkpoints',
        requireStaffPermission: 'cashflow_pricing',
      },
    ],
  },
  {
    icon: <Icon icon="security" />,
    title: 'SuperUser',
    requireSuperUser: true,
    uiKey: uiKey + 'super_user',
    cards: [
      {
        title: 'Global Users',
        url: '/global_users',
      },
      {
        title: 'Global Contacts',
        url: '/global_contacts',
      },
      {
        title: 'Payment Factors',
        url: '/payment_factors',
      },
    ],
  },
]

type MenuItem = {
  icon?: React.ReactNode
  title: string
  url?: string
  uiKey?: string
  betaChip?: boolean
  requireSuperUser?: boolean
  requireAdmin?: boolean
  requireNearmapOrg?: boolean
  requirePartnerFeature?: boolean
  requireMarketingServices?: boolean
  requireSeller?: boolean
  requireExpo?: boolean
  requireShowcase?: boolean
  requireProFeatures?: boolean
  requireProFeaturesUnlessStaff?: boolean
  requireStaff?: boolean
  requireStaffPermission?: keyof StaffPermissions
  requireCustomForms?: boolean
  requireWallet?: boolean
  requireConnectedOrgs?: boolean
  requireAccessRight?: PermissionKey
  chip?: React.ReactNode
  requireGoogleLeads?: boolean
  requireProfile?: boolean
  warningIcon?: React.ReactNode
  requireAdaStudioTemplates?: boolean
  requireProHardwareDistributor?: boolean
  proHardwareSupplierPreferenceEnabled?: boolean
  requireSupplierBaseFeature?: boolean
  requireProductUploadFeature?: boolean
  requireQuoteReviewFeature?: boolean
  requireShopApiDocsFeature?: boolean
  requireReportingAndInsights?: boolean
  requireProDashboard?: boolean
  requireProDashboardCustom?: boolean
  externalLink?: boolean
  requireSecurityAuditing?: boolean
  requireAds?: boolean
  isDivider?: boolean
  requireSchedulingV3?: boolean
  requireCentralizedNotifications?: boolean
  cards?: MenuItem[]
}

type ShowableMenuItem = Omit<MenuItem, 'cards'> & {
  shouldShow: boolean
  cards?: ShowableMenuItem[]
}
